/**
 * "Featured Add-ons" horizontal carousel. Presentational — the featured add-ons are resolved
 * service-level at build time and passed in via props. Reads MarketContext only to build the
 * "+ Book" deep-link. Renders nothing when there are no featured add-ons.
 * Styled to match the Figma "Featured Add-ons" (nodes 1491:4550 title/nav, 1491:4555 cards,
 * 1491:4556 card). The track is clipped to the page gutter and scrolled via the arrow controls.
 */
/* eslint-disable @next/next/no-img-element */
import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import styled from '@emotion/styled'
import { gsColors as c } from '../../styles/Variables'
import { mq } from '../../helpers'
import { formatCurrency } from '../../helpers/format'
import { useMarketContext } from '../../components/MarketContext'
import { bookServiceHref } from '../../helpers/bookLink'
import { useCarouselNav } from '../../helpers/useCarouselNav'

const CARD_WIDTH = 300
const CARD_GAP = 16

/**
 * @param {{ service: object, featuredAddOns?: Array<{ id, name, description, image, price, duration }> }} props
 */
const FeaturedAddons = ({ service, featuredAddOns = [] }) => {
  const { selectedMarket } = useMarketContext()
  const { trackRef, scrollBy, atStart, atEnd, hasOverflow } = useCarouselNav((CARD_WIDTH + CARD_GAP) * 2, featuredAddOns.length)

  if (!featuredAddOns.length) {
    return null
  }
  const href = bookServiceHref({ service, market: selectedMarket })

  return (
    <Wrapper>
      <div className="head">
        <h2 id="featured-addons">Featured Add-ons</h2>
        {hasOverflow && (
          <div className="nav">
            <button type="button" aria-label="Previous add-ons" onClick={() => scrollBy(-1)} disabled={atStart}>
              <img src="/service-detail/arrow.svg" alt="" aria-hidden="true" width={18} height={18} />
            </button>
            <button type="button" aria-label="Next add-ons" className="next" onClick={() => scrollBy(1)} disabled={atEnd}>
              <img src="/service-detail/arrow.svg" alt="" aria-hidden="true" width={18} height={18} />
            </button>
          </div>
        )}
      </div>

      <div className="track" ref={trackRef}>
        {featuredAddOns.map(addOn => (
          <article className="card" key={addOn.id}>
            <div className="img">
              {addOn.image && <Image src={addOn.image} alt={addOn.name} fill sizes={`${CARD_WIDTH}px`} style={{ objectFit: 'cover' }} />}
            </div>
            <div className="panel">
              <div className="content">
                <h3>{addOn.name}</h3>
                <p className="dur">
                  <img src="/service-detail/icon-duration.svg" alt="" aria-hidden="true" width={24} height={24} />
                  +{addOn.duration} min
                </p>
                <p className="desc">{addOn.description}</p>
              </div>
              <div className="foot">
                <span className="price">{formatCurrency(addOn.price)}</span>
                <Link href={href} legacyBehavior>
                  <a className="book">
                    <img src="/service-detail/icon-plus.svg" alt="" aria-hidden="true" width={16} height={16} />
                    Book
                  </a>
                </Link>
              </div>
            </div>
          </article>
        ))}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  padding: 64px 88px;
  overflow: hidden;
  ${mq('max', '900px')} {
    padding: 40px 24px;
  }
  ${mq('max', '600px')} {
    padding: 40px 0;
  }

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 32px;
    ${mq('max', '600px')} {
      padding: 0 24px;
    }
  }

  h2 {
    font-family: Garnett;
    font-size: 24px;
    font-weight: 600;
    line-height: 36px;
    text-transform: capitalize;
    color: ${c.black};
    margin: 0;
  }

  .nav {
    display: flex;
    align-items: center;
    gap: 24px;
    flex-shrink: 0;
    ${mq('max', '600px')} {
      display: none;
    }
  }

  .nav button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    color: ${c.black};
  }
  .nav button.next img {
    transform: rotate(180deg);
  }
  .nav button:disabled {
    opacity: 0.35;
    cursor: default;
  }

  .track {
    display: flex;
    align-items: stretch;
    gap: ${CARD_GAP}px;
    min-width: 0;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    -ms-overflow-style: none;
    ${mq('max', '600px')} {
      padding: 0 24px;
      scroll-padding-inline: 24px;
    }
  }
  .track::-webkit-scrollbar {
    display: none;
  }

  .card {
    flex: 0 0 ${CARD_WIDTH}px;
    max-width: ${CARD_WIDTH}px;
    display: flex;
    flex-direction: column;
    scroll-snap-align: start;
  }

  .img {
    position: relative;
    height: 195px;
    background: #c8c8cc;
    border-radius: 4px 4px 0 0;
    overflow: hidden;
  }

  .panel {
    flex: 1;
    background: ${c.eggshell};
    box-shadow: 0 0 10px rgba(231, 221, 206, 0.4);
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    justify-content: space-between;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  h3 {
    font-family: Euclid;
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;
    color: ${c.black};
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .dur {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-family: Euclid;
    font-size: 16px;
    color: ${c.black};
    margin: 0;
  }

  .desc {
    font-family: Euclid;
    font-size: 14px;
    line-height: 20px;
    color: ${c.darkGrey};
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .price {
    font-family: Euclid;
    font-size: 16px;
    color: ${c.black};
  }

  .book {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 46px;
    padding: 0 16px;
    border: 1px solid ${c.cobalt};
    color: ${c.cobalt};
    font-family: Euclid;
    font-size: 16px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
  }
`

export default FeaturedAddons
